import { useEffect, useState, useRef } from 'react';
import type { MutableRefObject } from 'react';

type TargetRef<T extends HTMLElement = HTMLElement> = MutableRefObject<T | null>;

const useInView = <T extends HTMLElement = HTMLElement>(
  options: IntersectionObserverInit = {
    root: null,
    rootMargin: '0px',
    threshold: 0.5,
  },
  triggerOnce = false,
): [TargetRef<T>, boolean] => {
  const [inView, setInView] = useState(false);
  const targetRef: TargetRef<T> = useRef<T | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (triggerOnce) {
            observer.unobserve(entry.target);
          }
        } else {
          setInView(false);
        }
      });
    }, options);

    const currentTarget = targetRef.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [options, triggerOnce]);

  return [targetRef, inView];
};

export default useInView;
